// Word Search core: levels, directions, generation with a filler blocklist scan, and selection checks.
// Spec: .wiki/wiki/topics/game-word-search.md (Easy 10x10 E,S; Medium 12x12 +NE,SE; Hard 15x15 all 8).
import { seeded, shuffle } from "../lib/rng"

export type WLevel = "easy" | "medium" | "hard"
export const WLEVELS: WLevel[] = ["easy", "medium", "hard"]
export type Dir = readonly [number, number] // [dRow, dCol]

const E: Dir = [0, 1], S: Dir = [1, 0], NE: Dir = [-1, 1], SE: Dir = [1, 1]
export const ALL_DIRS: Dir[] = [E, S, NE, SE, [0, -1], [-1, 0], [1, -1], [-1, -1]]
export const LEVEL_SPEC: Record<WLevel, { size: number; words: number; dirs: Dir[]; maxLen: number }> = {
  easy: { size: 10, words: 8, dirs: [E, S], maxLen: 9 },
  medium: { size: 12, words: 10, dirs: [E, S, NE, SE], maxLen: 11 },
  hard: { size: 15, words: 12, dirs: ALL_DIRS, maxLen: 12 },
}

export interface Placement { w: string; r: number; c: number; dr: number; dc: number }
export interface WordPuzzle { id: string; level: WLevel; theme: string; size: number; rows: string[]; words: Placement[] }

// English letter frequencies (approximate, per mille) so filler looks like ordinary text, not noise.
const FREQ = "E127T91A82O75I70N67S63H61R60D43L40C28U28M24W24F22G20Y20P19B15V10K8J2X2Q1Z1"
const LETTERS: string[] = (() => {
  const out: string[] = []
  for (const m of FREQ.matchAll(/([A-Z])(\d+)/g)) for (let i = 0; i < Number(m[2]); i++) out.push(m[1]!)
  return out
})()

/** Every straight line of the grid read in all 8 directions, as strings. */
export function allLines(rows: string[]): string[] {
  const n = rows.length
  const at = (r: number, c: number): string => rows[r]![c]!
  const lines: string[] = []
  for (let r = 0; r < n; r++) lines.push(rows[r]!)
  for (let c = 0; c < n; c++) lines.push(rows.map((row) => row[c]!).join(""))
  for (let k = -(n - 1); k <= n - 1; k++) {
    let d = "", a = ""
    for (let r = 0; r < n; r++) {
      const c1 = r + k, c2 = n - 1 - r + k
      if (c1 >= 0 && c1 < n) d += at(r, c1)
      if (c2 >= 0 && c2 < n) a += at(r, c2)
    }
    lines.push(d, a)
  }
  return lines.flatMap((l) => [l, [...l].reverse().join("")])
}

export function countOccurrences(rows: string[], word: string): number {
  let n = 0
  for (const line of allLines(rows)) {
    for (let i = line.indexOf(word); i !== -1; i = line.indexOf(word, i + 1)) n++
  }
  // A palindrome is counted once per direction pair, so halve it.
  return word === [...word].reverse().join("") ? n / 2 : n
}

export function findBlocked(rows: string[], blocklist: readonly string[]): string[] {
  const lines = allLines(rows)
  return blocklist.filter((b) => lines.some((l) => l.includes(b)))
}

export function generate(
  words: readonly string[], level: WLevel, seed: string, blocklist: readonly string[], theme = "",
): WordPuzzle | null {
  const spec = LEVEL_SPEC[level]
  const rand = seeded(`ws:${seed}`)
  // No list word inside another, forwards or backwards (RAIN in RAINBOW, BOARD in GAMEBOARD): the short one could
  // only be found by re-selecting part of the long one (bug hunt 2026-10-08).
  const nested = (a: string, b: string): boolean => {
    const rb = [...b].reverse().join("")
    return b.includes(a) || rb.includes(a)
  }
  const pool: string[] = []
  for (const w of shuffle(words.filter((x) => x.length <= spec.maxLen && x.length <= spec.size), rand)) {
    if (pool.length === spec.words) break
    if (!pool.some((p) => nested(w, p) || nested(p, w))) pool.push(w)
  }
  if (pool.length < spec.words) return null
  pool.sort((a, b) => b.length - a.length)
  for (let attempt = 0; attempt < 60; attempt++) {
    const grid: (string | null)[][] = Array.from({ length: spec.size }, () => new Array(spec.size).fill(null))
    const placed: Placement[] = []
    let ok = true
    for (const w of pool) {
      let done = false
      for (let tries = 0; tries < 300 && !done; tries++) {
        const [dr, dc] = spec.dirs[Math.floor(rand() * spec.dirs.length)]!
        const r = Math.floor(rand() * spec.size), c = Math.floor(rand() * spec.size)
        const er = r + dr * (w.length - 1), ec = c + dc * (w.length - 1)
        if (er < 0 || er >= spec.size || ec < 0 || ec >= spec.size) continue
        let fits = true
        for (let k = 0; k < w.length; k++) {
          const cell = grid[r + dr * k]![c + dc * k]
          if (cell !== null && cell !== w[k]) { fits = false; break }
        }
        if (!fits) continue
        for (let k = 0; k < w.length; k++) grid[r + dr * k]![c + dc * k] = w[k]!
        placed.push({ w, r, c, dr, dc })
        done = true
      }
      if (!done) { ok = false; break }
    }
    if (!ok) continue
    // Fill, then repair: re-randomise only filler cells inside any blocked match (all 8 directions).
    const fixed = grid.map((row) => row.map((ch) => ch !== null))
    const cells = grid.map((row) => row.map((ch) => ch ?? LETTERS[Math.floor(rand() * LETTERS.length)]!))
    const blocked = new Set(blocklist)
    const lens = [...new Set(blocklist.map((b) => b.length))]
    const lines = lineCoords(spec.size)
    let repaired = false
    for (let pass = 0; pass < 400; pass++) {
      const hit = firstHit(cells, lines, blocked, lens)
      if (!hit) { repaired = true; break }
      const free = hit.filter(([r, c]) => !fixed[r]![c])
      if (!free.length) break // the bad word is made only of list-word letters: start over
      const [r, c] = free[Math.floor(rand() * free.length)]!
      cells[r]![c] = LETTERS[Math.floor(rand() * LETTERS.length)]!
    }
    if (!repaired) continue
    const rows = cells.map((row) => row.join(""))
    if (pool.some((w) => countOccurrences(rows, w) !== 1)) continue
    const sortedWords = placed.slice().sort((a, b) => a.w.localeCompare(b.w))
    return { id: seed, level, theme, size: spec.size, rows, words: sortedWords }
  }
  return null
}

/** Coordinates of every straight line in the grid (rows, columns, both diagonals), one direction each. */
export function lineCoords(n: number): [number, number][][] {
  const out: [number, number][][] = []
  for (let r = 0; r < n; r++) out.push(Array.from({ length: n }, (_, c) => [r, c] as [number, number]))
  for (let c = 0; c < n; c++) out.push(Array.from({ length: n }, (_, r) => [r, c] as [number, number]))
  for (let k = -(n - 1); k <= n - 1; k++) {
    const d: [number, number][] = [], a: [number, number][] = []
    for (let r = 0; r < n; r++) {
      if (r + k >= 0 && r + k < n) d.push([r, r + k])
      if (n - 1 - r + k >= 0 && n - 1 - r + k < n) a.push([r, n - 1 - r + k])
    }
    out.push(d, a)
  }
  return out
}

/** First blocked word found reading any line forwards or backwards; returns its cells. */
function firstHit(cells: string[][], lines: [number, number][][], blocked: Set<string>, lens: number[]): [number, number][] | null {
  for (const line of lines) {
    const text = line.map(([r, c]) => cells[r]![c]!).join("")
    const rev = [...text].reverse().join("")
    for (const L of lens) {
      for (let i = 0; i + L <= text.length; i++) {
        if (blocked.has(text.slice(i, i + L))) return line.slice(i, i + L)
        if (blocked.has(rev.slice(i, i + L))) return line.slice(text.length - i - L, text.length - i)
      }
    }
  }
  return null
}

/** The letters between two cells if they lie on one of the 8 lines, else null. */
export function lineBetween(size: number, r1: number, c1: number, r2: number, c2: number): [number, number][] | null {
  const dr = Math.sign(r2 - r1), dc = Math.sign(c2 - c1)
  const len = Math.max(Math.abs(r2 - r1), Math.abs(c2 - c1))
  if (!(r1 === r2 || c1 === c2 || Math.abs(r2 - r1) === Math.abs(c2 - c1))) return null
  const out: [number, number][] = []
  for (let k = 0; k <= len; k++) {
    const r = r1 + dr * k, c = c1 + dc * k
    if (r < 0 || c < 0 || r >= size || c >= size) return null
    out.push([r, c])
  }
  return out
}

/** Snap an imprecise end cell onto the nearest of the 8 directions from the start (for shaky taps). */
export function snapEnd(size: number, r1: number, c1: number, r2: number, c2: number): [number, number] {
  const dr = r2 - r1, dc = c2 - c1
  if (dr === 0 && dc === 0) return [r1, c1]
  const angle = Math.atan2(dr, dc)
  const step = Math.round(angle / (Math.PI / 4))
  const dirs: Dir[] = [[0, 1], [1, 1], [1, 0], [1, -1], [0, -1], [-1, -1], [-1, 0], [-1, 1]]
  const [sr, sc] = dirs[((step % 8) + 8) % 8]!
  const len = Math.max(Math.abs(dr), Math.abs(dc))
  const r = Math.min(size - 1, Math.max(0, r1 + sr * len)), c = Math.min(size - 1, Math.max(0, c1 + sc * len))
  return [r, c]
}

/** Which unfound word (if any) a selection spells, forwards or backwards. */
export function matchWord(p: WordPuzzle, cells: [number, number][], found: ReadonlySet<string>): Placement | null {
  for (const pl of p.words) {
    if (found.has(pl.w) || pl.w.length !== cells.length) continue
    const fw = Array.from({ length: pl.w.length }, (_, k) => [pl.r + pl.dr * k, pl.c + pl.dc * k] as [number, number])
    const same = (a: [number, number][]): boolean => a.every(([r, c], k) => r === cells[k]![0] && c === cells[k]![1])
    if (same(fw) || same(fw.slice().reverse())) return pl
  }
  return null
}
